import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { Input } from '@econsult/econsult-ui/dist/components/Input';
import {
  EndIcon,
  InputElement,
  InputLabel,
  InputMessage,
  InputMessageWrapper,
  InputWrapper,
} from '@econsult/econsult-ui/dist/components/Input/InputStyles';
import { PopOutBody } from '@econsult/econsult-ui/dist/components/PopOut';
import { Div } from '@econsult/econsult-ui/dist/components/shared/div';
import { Paragraph } from '@econsult/econsult-ui/dist/components/shared/typography';
import { ArrowDownIcon } from 'assets';
import { ColumnDiv, FlexDiv } from 'components/Div/styles';
import styled from 'styled-components';
import { ActionPopOutSearchInputWrapper, InputButton } from 'styles/elements/popout';

const templateManagerWidth = '200px';

export const StyledArrowDown = styled(ArrowDownIcon)`
  width: 22px !important;
  height: 22px !important;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.colorPrimaryPrimary100};
`;

export const InnerWrapper = styled(Div)``;

export const StyledPopOutBody = styled(PopOutBody)`
  max-width: ${templateManagerWidth};
  overflow-x: hidden;
  && {
    padding: 0;
    animation: appear 0.2s ease;
  }

  @keyframes appear {
    from {
      transform: translateY(0.5rem);
      opacity: 0;
    }

    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const borderRadiusCurve = '6px';
export const ActionPopOutScrollContainer = styled(ColumnDiv)`
  display: flex;
  max-height: 16rem;
  overflow-y: scroll;
  border-radius: 0 0 ${borderRadiusCurve} ${borderRadiusCurve};
`;

export const PopOutScrollView = styled(ActionPopOutScrollContainer)`
  max-height: 300px;
  width: ${templateManagerWidth};
  padding: 0;
`;

export const HeaderWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colorBackgroundSoftWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colorNeutralNeutral9};

  &:first-child {
    border-radius: 0.375rem 0.375rem 0 0;
  }
`;

export const ItemWrapper = styled(Div)`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  padding: ${({ small }) => (small ? '0.75rem' : '1rem 0.75rem')};
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  opacity: ${({ $inert }) => ($inert ? 0.5 : 1)};
  border-bottom: 1px solid ${({ theme }) => theme.colorNeutralNeutral9};

  &:hover {
    background: ${({ theme, $inert }) => ($inert ? 'initial' : theme.colorNeutralNeutral9)};
  }

  button:not(:last-child) & {
    border-bottom: 1px solid ${({ theme }) => theme.colorNeutralNeutral9};
  }
`;

export const FooterManageButtonContainer = styled(Div)`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
  background: #F8F6F7;
`;

export const ListItemContainer = styled(Div)`
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
  min-height: 42px;
  max-height: 42px;
  width: ${templateManagerWidth};
  padding: 14px 8px;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${({ theme }) => `2px solid ${theme.colorPrimaryPrimary10}`};
  background: ${({ theme, isHighlighted }) => isHighlighted && theme.colorStatusInfoBg};
  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.colorPrimaryPrimary10};
  }
`;

export const TemplateNameText = styled(Paragraph).attrs({ typography: 'ParagraphTinyHeavy' })`
  color: ${({ theme }) => theme.colorPrimaryPrimary100};
  text-overflow: ellipsis;
  max-width: 120px;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
  padding: 0;
`;

export const TemplateTypeText = styled(Paragraph).attrs({ typography: 'ParagraphTinyRegular' })`
  color: ${({ theme }) => theme.colorPrimaryPrimary70};
  margin: 0;
  padding: 0;
`;

export const NewTemplateButtonContainer = styled(FlexDiv)`
  flex: 1;
  height: 60px;
  justify-content: flex-start;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colorNeutralNeutral8};
`;

export const SidebarButton = styled(Button).attrs({
  fillStyle: 'borderless',
  size: 'small',
  iconPosition: 'left',
})`
  outline: none !important;
  height: 32px;

  &:focus-within {
    background: ${({ theme }) => theme.colorNeutralNeutral9};
  }
`;

export const SidebarTab = styled.li.attrs({ typography: 'ParagraphTinyHeavy' })`
  width: 100%;
  && {
    padding: 0;
  }
  display: flex;
  align-items: center;
  flex: 1;
`;

export const SidebarTabs = styled.ul`
  margin: 0;
  padding: 0.25rem 0.25rem 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-evenly;
  list-style: none;
  box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.colorNeutralNeutral8};
`;

export const SidebarTabButton = styled(Button).attrs({
  fillStyle: 'borderless',
  size: 'small',
})`
  && {
    padding: 0.375rem 0.75rem 0.375rem 0.5rem;
    display: flex;
    flex: 1;
    align-items: center;
    gap: 0.25rem;
    ${({ theme }) => theme.fontStyles};
    line-height: 1;
    color: ${({ isActive, theme }) => (isActive ? theme.colorNeutraBlack : theme.colorNeutralNeutral3)};
    background: ${({ isActive, theme }) => (isActive ? theme.colorBackgroundSoftWhite : 'initial')};
    border-radius: 0.25rem 0.25rem 0 0;
    border: none;
    border-top: ${({ isActive, theme }) => isActive && `1px solid ${theme.matchingContainerBorderColor}`};
    border-left: ${({ isActive, theme }) => isActive && `1px solid ${theme.matchingContainerBorderColor}`};
    border-right: ${({ isActive, theme }) => isActive && `1px solid ${theme.matchingContainerBorderColor}`};

    &:focus-within {
      outline: none;
    }

    * {
      pointer-events: none;
    }

    svg {
      margin: 0;
      width: 1rem;
      height: 1rem;
    }
  }
`;

export const TemplateSearch = styled(ActionPopOutSearchInputWrapper)`
  ${InputWrapper} {
    gap: 0;
  }

  ${EndIcon} {
    --icon-size: 1rem;
    --icon-margin: 0.75rem;

    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colorNeutralNeutral8};

    ${InputButton} {
      padding: 0;
      color: currentColor;
      border: none;
    }

    svg {
      margin: 0;
      width: var(--icon-size);
      height: var(--icon-size);
      color: currentColor;
    }
  }
`;

export const SidebarSearch = styled(TemplateSearch)`
  padding: 0.75rem 0.75rem 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundSoftWhite};
`;

export const TextInput = styled(Input).attrs({
  type: 'text',
  inputMode: 'text',
})`
  &${InputWrapper} {
    column-gap: 0;
    row-gap: 0.25rem;
    grid-column: 1 / -1;
  }

  ${InputLabel} {
    font-size: 0.75rem;
    line-height: 1.2;
  }

  ${InputElement} {
    --input-color: ${({ theme }) => theme.colorNeutralNeutral7};
    font-size: 0.75rem;
    font-weight: 400;
    box-shadow: inset 0 0 0 1px var(--input-color);
  }
  ${InputMessage} {
    font-size: 10px;
  }
  ${InputMessageWrapper} {
    padding: 0 10px;
  }
`;

interface TemplateManagerMainContentProps {
  isEmpty: boolean;
}

export const TemplateManagerMainContent = styled.section<TemplateManagerMainContentProps>`
  box-sizing: border-box;
  width: 100%;
  padding: 1rem 1.5rem;
  display: grid;
  align-content: ${({ isEmpty }) => (isEmpty ? 'center' : 'start')};
  gap: 1.5rem;
  overflow-y: scroll;
`;

export const MainFooter = styled.section`
  padding-inline: 1.25rem;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.colorNeutralNeutral8};
`;

export const SaveTemplateButton = styled(Button).attrs({
  size: 'small',
  state: 'secondary',
})``;

export const DeleteTemplateButton = styled(Button).attrs({
  size: 'small',
  fillStyle: 'borderless',
})`
  color: ${({ theme }) => theme.colorStatusError};
`;

export const NoTemplateSelectedText = styled(Paragraph)`
  width: 100%;
  height: 100%;
  font-size: 0.875rem;
  font-weight: 400;
  text-align: center;
  color: ${({ theme }) => theme.colorNeutralNeutral3};
`;

export const PracticeRoleInfo = styled(Paragraph).attrs({ typography: 'ParagraphTinyRegular' })`
  color: ${({ theme }) => theme.colorPrimaryPrimary100};
  margin: 0;
  padding-top: 4px;
`;
